import {isNil, isNotNil} from 'ramda';

import i18n from '../../../../../i18n/i18n';
import {
  NumericBoundBody,
  RelativeFilterChoice,
  RelativeFilterConfigBody,
} from '../../../../../types/api/api';
import {type IsKnownPresetFeature, getFeaturesPresetTags} from './normalizePresetFeatures';

export type GetPresetTagsOptions = {
  getFeatureLabel?: (featureKey: string) => string;
  isKnownFeature?: IsKnownPresetFeature;
};

const PCT_UNIT = '%';

function boundSummary(
  bound: NumericBoundBody | null,
  unitKey: string,
  sameKey: string
): string | null {
  if (isNil(bound)) {
    return null;
  }

  const unit = unitKey === PCT_UNIT ? PCT_UNIT : i18n.t(unitKey);
  const parts: string[] = [];

  if (isNotNil(bound.from) && bound.from !== 0) {
    parts.push(`${bound.from}${unit}`);
  }
  if (isNotNil(bound.to) && bound.to !== 0) {
    const sign = bound.to > 0 ? '+' : '';
    parts.push(`${sign}${bound.to}${unit}`);
  }

  if (parts.length === 0 && (bound.from === 0 || bound.to === 0)) {
    return i18n.t(sameKey);
  }

  return parts.length > 0 ? parts.join(', ') : null;
}

function withParameterLabel(parameterI18nKey: string, summary: string | null): string | null {
  if (isNil(summary)) {
    return null;
  }
  return `${i18n.t(parameterI18nKey)}: ${summary}`;
}

function withParameterLabelUnlessSame(
  parameterI18nKey: string,
  summary: string | null,
  sameKey: string
): string | null {
  return summary === i18n.t(sameKey) ? summary : withParameterLabel(parameterI18nKey, summary);
}

function boundPairSummary(
  abs: NumericBoundBody | null,
  pct: NumericBoundBody | null,
  absUnitKey: string,
  parameterI18nKey: string,
  sameKey: string
): string | null {
  const absSummary = isNotNil(abs) ? boundSummary(abs, absUnitKey, sameKey) : null;
  const pctSummaryValue = isNotNil(pct) ? boundSummary(pct, PCT_UNIT, sameKey) : null;
  const pctSummary = withParameterLabelUnlessSame(parameterI18nKey, pctSummaryValue, sameKey);

  return isNotNil(absSummary) ? absSummary : pctSummary;
}

function propSummary(
  mode: RelativeFilterChoice | null,
  sameKey: string,
  sameBetterKey: string
): string | null {
  if (isNil(mode)) {
    return null;
  }
  return i18n.t(mode === 'same' ? sameKey : sameBetterKey);
}

/**
 * Builds the list of human-readable tags shown in the preset summary.
 *
 * Note: `config.sellerType` is intentionally omitted to mirror the UI — the
 * seller-type chips in `PropertiesSection` are hidden until we can read the
 * vehicle's seller type (see the TODO there). Surfacing a tag for a field the
 * user can't see or edit would be confusing. Re-add the entry here when the
 * UI section is re-enabled.
 */
export function getPresetTags(
  config: RelativeFilterConfigBody,
  options?: GetPresetTagsOptions
): string[] {
  const parts: string[] = [];

  const yearSummary = boundSummary(
    config.year,
    'entity.filterForm.savedFilters.summary.unit.year',
    'entity.filterForm.savedFilters.summary.registration.same'
  );
  if (isNotNil(yearSummary)) {
    parts.push(yearSummary);
  }

  const mileageSummary = boundPairSummary(
    config.mileage,
    config.mileagePct,
    'entity.filterForm.savedFilters.summary.unit.km',
    'entity.filterForm.millage',
    'entity.filterForm.savedFilters.summary.mileage.same'
  );
  if (isNotNil(mileageSummary)) {
    parts.push(mileageSummary);
  }

  const powerSummary = boundPairSummary(
    config.power,
    config.powerPct,
    'entity.filterForm.savedFilters.summary.unit.kw',
    'entity.filterForm.power',
    'entity.filterForm.savedFilters.summary.power.same'
  );
  if (isNotNil(powerSummary)) {
    parts.push(powerSummary);
  }

  const priceSummary = boundPairSummary(
    config.price,
    config.pricePct,
    'entity.filterForm.savedFilters.summary.unit.eur',
    'entity.filterForm.price',
    'entity.filterForm.savedFilters.summary.price.same'
  );
  if (isNotNil(priceSummary)) {
    parts.push(priceSummary);
  }

  const featureScoreSameKey = 'entity.filterForm.savedFilters.summary.featureScore.same';
  const featureScoreSummary = withParameterLabelUnlessSame(
    'entity.filterForm.featureScore',
    boundSummary(config.featureScore, PCT_UNIT, featureScoreSameKey),
    featureScoreSameKey
  );
  if (isNotNil(featureScoreSummary)) {
    parts.push(featureScoreSummary);
  }

  if (isNotNil(options?.getFeatureLabel) && isNotNil(options?.isKnownFeature)) {
    parts.push(
      ...getFeaturesPresetTags(config.features, options.getFeatureLabel, options.isKnownFeature)
    );
  }

  if (isNotNil(config.radius)) {
    parts.push(i18n.t('entity.filterForm.savedFilters.summary.radius', {km: config.radius}));
  }

  const trimLabel = propSummary(
    config.trimLevel,
    'entity.filterForm.savedFilters.summary.trimLevel.same',
    'entity.filterForm.savedFilters.summary.trimLevel.sameBetter'
  );
  if (isNotNil(trimLabel)) {
    parts.push(trimLabel);
  }

  const transLabel = propSummary(
    config.transmission,
    'entity.filterForm.savedFilters.summary.transmission.same',
    'entity.filterForm.savedFilters.summary.transmission.sameBetter'
  );
  if (isNotNil(transLabel)) {
    parts.push(transLabel);
  }

  const fuelLabel = propSummary(
    config.fuel,
    'entity.filterForm.savedFilters.summary.fuel.same',
    'entity.filterForm.savedFilters.summary.fuel.sameBetter'
  );
  if (isNotNil(fuelLabel)) {
    parts.push(fuelLabel);
  }

  const bodyLabel = propSummary(
    config.body,
    'entity.filterForm.savedFilters.summary.body.same',
    'entity.filterForm.savedFilters.summary.body.sameBetter'
  );
  if (isNotNil(bodyLabel)) {
    parts.push(bodyLabel);
  }

  const driveLabel = propSummary(
    config.drive,
    'entity.filterForm.savedFilters.summary.drive.same',
    'entity.filterForm.savedFilters.summary.drive.sameBetter'
  );
  if (isNotNil(driveLabel)) {
    parts.push(driveLabel);
  }

  const vatLabel = propSummary(
    config.vatDeductible,
    'entity.filterForm.savedFilters.summary.vatDeductible.same',
    'entity.filterForm.savedFilters.summary.vatDeductible.sameBetter'
  );
  if (isNotNil(vatLabel)) {
    parts.push(vatLabel);
  }

  const seatsLabel = propSummary(
    config.seats,
    'entity.filterForm.savedFilters.summary.seats.same',
    'entity.filterForm.savedFilters.summary.seats.sameBetter'
  );
  if (isNotNil(seatsLabel)) {
    parts.push(seatsLabel);
  }

  return parts;
}
